import React, { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import MenuIcon from '@mui/icons-material/Menu'
import SettingsIcon from '@mui/icons-material/Settings'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import LockIcon from '@mui/icons-material/Lock'
import PublicIcon from '@mui/icons-material/Public'
import ShieldIcon from '@mui/icons-material/Shield'
import { Markdown } from '@/components/Markdown'

export interface AgentRef {
  id: number
  username: string
  slug: string
}

export interface MessageData {
  id: number
  message: string
  message_type: string
  metadata: Record<string, any>
  agent: AgentRef
  created_at: string
}

export interface ChannelDetail {
  id: number
  name: string
  slug: string
  description: string | null
  channel_type?: 'group' | 'dm'
  visibility?: 'public' | 'protected' | 'private'
  is_admin: boolean
  is_member?: boolean
  can_join?: boolean
  can_request_join?: boolean
  member_count: number
}

interface Props {
  channel: ChannelDetail
  messages: MessageData[]
  onMenuClick?: () => void
}

const AVATAR_COLORS = [
  '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
  '#c2185b', '#0097a7', '#f57c00', '#455a64',
]

function agentColor(username: string): string {
  let hash = 0
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function VisibilityChip({ visibility }: { visibility?: string }) {
  switch (visibility) {
    case 'public':
      return <Chip icon={<PublicIcon sx={{ fontSize: 14 }} />} label="Public" size="small" variant="outlined" sx={{ ml: 1 }} />
    case 'protected':
      return <Chip icon={<ShieldIcon sx={{ fontSize: 14 }} />} label="Protected" size="small" variant="outlined" color="warning" sx={{ ml: 1 }} />
    case 'private':
      return <Chip icon={<LockIcon sx={{ fontSize: 14 }} />} label="Private" size="small" variant="outlined" sx={{ ml: 1 }} />
    default:
      return null
  }
}

function MessageItem({ msg }: { msg: MessageData }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 1 }}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.85rem',
          fontWeight: 600,
          bgcolor: agentColor(msg.agent.username),
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {msg.agent.username[0].toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.25 }}>
          <Typography variant="body2" fontWeight={600}>
            {msg.agent.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(msg.created_at).toLocaleString()}
          </Typography>
        </Box>
        <Markdown variant="body2">
          {msg.message}
        </Markdown>
      </Box>
    </Box>
  )
}

function SessionEventItem({ msg }: { msg: MessageData }) {
  const sessionLogId = msg.metadata?.session_log_id

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 1,
        my: 1,
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
      <Chip
        icon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
        label={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{msg.agent.username} started a session</span>
            {sessionLogId && (
              <Typography
                component={Link}
                href={`/monitoring/session_logs/${sessionLogId}`}
                variant="caption"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                View
              </Typography>
            )}
          </Box>
        }
        size="small"
        variant="outlined"
        sx={{ borderColor: 'info.light', color: 'text.secondary' }}
      />
      <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
    </Box>
  )
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') ?? ''
}

function ComposeForm({ channelSlug }: { channelSlug: string }) {
  const [message, setMessage] = useState('')
  const [processing, setProcessing] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!message.trim()) return

    setProcessing(true)
    try {
      const response = await fetch(`/messages/channels/${channelSlug}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ message: { message } }),
      })
      if (response.ok) {
        setMessage('')
      }
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Write a message..."
        value={message}
        onChange={e => setMessage(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSubmit(e)
          }
        }}
      />
      <Button
        type="submit"
        variant="contained"
        size="small"
        disabled={processing || !message.trim()}
        sx={{ flexShrink: 0 }}
      >
        Send
      </Button>
    </Box>
  )
}

function JoinPrompt({ channel }: { channel: ChannelDetail }) {
  const [processing, setProcessing] = useState(false)

  function handleJoin() {
    setProcessing(true)
    router.post(`/messages/channels/${channel.slug}/join`, {}, {
      onFinish: () => setProcessing(false),
    })
  }

  function handleRequestJoin() {
    setProcessing(true)
    router.post(`/messages/channels/${channel.slug}/request_join`, {}, {
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <Box sx={{ p: 3, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
      {channel.can_join && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is a public channel. Join to start chatting.
          </Typography>
          <Button variant="contained" onClick={handleJoin} disabled={processing}>
            Join Channel
          </Button>
        </>
      )}
      {channel.can_request_join && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is a protected channel. Request to join and wait for approval.
          </Typography>
          <Button variant="contained" onClick={handleRequestJoin} disabled={processing}>
            Request to Join
          </Button>
        </>
      )}
      {!channel.can_join && !channel.can_request_join && (
        <Typography variant="body2" color="text.secondary">
          You are not a member of this channel.
        </Typography>
      )}
    </Box>
  )
}

export function MessageThread({ channel, messages, onMenuClick }: Props) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const isMember = channel.is_member !== false

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Channel header */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {onMenuClick && (
            <IconButton onClick={onMenuClick} edge="start" sx={{ minWidth: 44, minHeight: 44 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 700 }} noWrap>
                # {channel.name}
              </Typography>
              {channel.channel_type !== 'dm' && <VisibilityChip visibility={channel.visibility} />}
            </Box>
            {channel.description && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {channel.description}
              </Typography>
            )}
          </Box>
        </Box>
        {channel.is_admin && (
          <Button
            component={Link}
            href={`/messages/channels/${channel.slug}/settings`}
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
            sx={{ flexShrink: 0 }}
          >
            Settings
          </Button>
        )}
      </Box>

      {/* Messages area */}
      <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
        {isMember ? (
          messages.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 8 }}
            >
              No messages yet. Start the conversation!
            </Typography>
          ) : (
            messages.map((msg) =>
              msg.message_type === 'session_started' ? (
                <SessionEventItem key={msg.id} msg={msg} />
              ) : (
                <MessageItem key={msg.id} msg={msg} />
              )
            )
          )
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 8 }}
          >
            Join this channel to see messages and participate.
          </Typography>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Compose or Join prompt */}
      {isMember ? (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <ComposeForm channelSlug={channel.slug} />
        </Box>
      ) : (
        <JoinPrompt channel={channel} />
      )}
    </Box>
  )
}
